import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, 
  User, 
  Bell, 
  Eye, 
  Shield, 
  Trash2,
  Lock,
  Globe,
  Target,
  Check,
  AlertTriangle,
  ChevronRight,
  BellRing
} from 'lucide-react';
import { getBaseUrl } from '@/utils/api';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const API_URL = getBaseUrl();

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner', description: 'New to presence work' },
  { value: 'intermediate', label: 'Intermediate', description: 'Some experience' },
  { value: 'advanced', label: 'Advanced', description: 'Experienced practitioner' },
];

const FONT_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'x-large', label: 'Extra Large' },
];

// Notifications Section Component with Push Notification Support
const NotificationsSection = ({ notifications, setNotifications, saveProfile, loading }) => {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading: pushLoading,
    error: pushError,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();
  const [testFeedback, setTestFeedback] = useState(null);

  const handlePushToggle = async (enabled) => {
    setTestFeedback(null);
    if (enabled) {
      await subscribe();
    } else {
      await unsubscribe();
    }
  };

  const handleTest = async () => {
    setTestFeedback({ tone: 'busy', text: 'Sending…' });
    const ok = await sendTestNotification();
    setTestFeedback(ok
      ? { tone: 'ok', text: 'Sent — check your lock screen.' }
      : { tone: 'err', text: 'Test push failed. See details below.' });
  };

  return (
    <div className="bg-white rounded-xl p-6 space-y-6">
      <div>
        <h2 className="font-display text-lg font-bold text-obsidian-night mb-1">Notifications</h2>
        <p className="text-sm text-shadow-pine/70">Choose what you want to hear from us</p>
      </div>

      {/* Push Notifications - Primary */}
      {isSupported && (
        <div className="p-4 bg-burnt-terra/5 rounded-lg border border-burnt-terra/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-burnt-terra/10 rounded-full flex items-center justify-center">
                <BellRing className="w-5 h-5 text-burnt-terra" />
              </div>
              <div>
                <p className="font-medium text-sm text-obsidian-night">Push Notifications</p>
                <p className="text-xs text-shadow-pine/70">
                  {isSubscribed 
                    ? 'You\'ll receive practice reminders on this device'
                    : 'Enable to get practice reminders even when the app is closed'
                  }
                </p>
                {permission === 'denied' && (
                  <p className="text-xs text-red-600 mt-1">
                    Notifications blocked. Please enable in browser settings.
                  </p>
                )}
                {pushError && (
                  <p className="text-xs text-red-600 mt-1">{pushError}</p>
                )}
              </div>
            </div>
            <Switch
              checked={isSubscribed}
              onCheckedChange={handlePushToggle}
              disabled={pushLoading || permission === 'denied'}
            />
          </div>
          
          {isSubscribed && (
            <div className="mt-3 pt-3 border-t border-burnt-terra/10">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTest}
                className="text-xs"
                data-testid="settings-test-notification"
              >
                Send Test Notification
              </Button>
              {testFeedback && (
                <p
                  className={`text-xs mt-2 ${testFeedback.tone === 'err' ? 'text-red-600' : testFeedback.tone === 'ok' ? 'text-shadow-pine' : 'text-shadow-pine/70'}`}
                  data-testid="settings-test-feedback"
                >
                  {testFeedback.text}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-sm text-obsidian-night">Session Reminders</p>
            <p className="text-xs text-shadow-pine/70">Gentle reminder when it&apos;s time to practice</p>
          </div>
          <Switch
            checked={notifications.session_reminders}
            onCheckedChange={(checked) => setNotifications({...notifications, session_reminders: checked})}
          />
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-sm text-obsidian-night">Inactivity Nudges</p>
            <p className="text-xs text-shadow-pine/70">A quiet check-in if we haven&apos;t seen you in a while</p>
          </div>
          <Switch
            checked={notifications.inactivity_nudges}
            onCheckedChange={(checked) => setNotifications({...notifications, inactivity_nudges: checked})}
          />
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="font-medium text-sm text-obsidian-night">Completion Acknowledgements</p>
            <p className="text-xs text-shadow-pine/70">Brief note when you finish a session</p>
          </div>
          <Switch
            checked={notifications.completion_acknowledgements}
            onCheckedChange={(checked) => setNotifications({...notifications, completion_acknowledgements: checked})}
          />
        </div>

        {notifications.session_reminders && (
          <div className="pt-2">
            <Label className="text-sm font-medium text-obsidian-night">Reminder Time</Label>
            <Input
              type="time"
              value={notifications.reminder_time}
              onChange={(e) => setNotifications({...notifications, reminder_time: e.target.value})}
              className="mt-1 w-40"
            />
          </div>
        )}
      </div>

      <Button onClick={saveProfile} disabled={loading} className="bg-burnt-terra hover:bg-burnt-terra/90">
        {loading ? 'Saving...' : 'Save Preferences'}
      </Button>
    </div>
  );
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useUser();
  const [activeSection, setActiveSection] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [timezone, setTimezone] = useState(user?.profile?.timezone || 'America/New_York');
  const [coachingGoal, setCoachingGoal] = useState(user?.profile?.coaching_goal || '');
  const [experienceLevel, setExperienceLevel] = useState(user?.profile?.experience_level || 'beginner');
  
  // Notification state
  const [notifications, setNotifications] = useState({
    session_reminders: true,
    inactivity_nudges: true,
    completion_acknowledgements: true,
    reminder_time: '09:00',
    ...user?.profile?.notifications
  });
  
  // Accessibility state
  const [accessibility, setAccessibility] = useState({
    font_size: 'medium',
    high_contrast: false,
    reduce_motion: false,
    captions_enabled: false,
    ...user?.profile?.accessibility
  });
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const saveProfile = async () => {
    setLoading(true);
    setError('');
    setSaved(false);
    
    try {
      const response = await fetch(`${API_URL}/api/me/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name,
          timezone,
          coaching_goal: coachingGoal,
          experience_level: experienceLevel,
          notifications,
          accessibility
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save');
      }
      
      if (data.user && updateUser) {
        updateUser(data.user);
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordForm.new.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }
      
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    try {
      const response = await fetch(`${API_URL}/api/me/account`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        logout();
        navigate('/');
      }
    } catch (err) {
      setError('Failed to delete account');
    }
  };

  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'accessibility', label: 'Accessibility', icon: Eye },
    { id: 'account', label: 'Account & Security', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-ivory-mist">
      {/* Header */}
      <header className="border-b border-burnt-terra/20 bg-white/80 backdrop-blur-sm sticky top-0 z-10 pt-safe">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-shadow-pine hover:text-burnt-terra -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-display text-xl font-bold text-obsidian-night">Settings</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <nav className="md:w-56 flex-shrink-0">
            <div className="bg-white rounded-xl p-2 space-y-1">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-burnt-terra/10 text-burnt-terra'
                      : 'text-shadow-pine hover:bg-ivory-mist'
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  <span className="font-body text-sm">{section.label}</span>
                </button>
              ))}
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1">
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <div className="bg-white rounded-xl p-6 space-y-6">
                <div>
                  <h2 className="font-display text-lg font-bold text-obsidian-night mb-1">Profile</h2>
                  <p className="text-sm text-shadow-pine/70">Your basic information and preferences</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-obsidian-night">Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-obsidian-night">Email</Label>
                    <Input
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="mt-1 bg-ivory-mist"
                    />
                    <p className="text-xs text-shadow-pine/60 mt-1">Contact support to change your email</p>
                  </div>

                  <div>
                    <Label htmlFor="timezone" className="text-sm font-medium text-obsidian-night">Timezone</Label>
                    <select
                      id="timezone"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm"
                    >
                      {TIMEZONES.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="goal" className="text-sm font-medium text-obsidian-night">
                      Coaching Goal <span className="text-shadow-pine/50">(optional)</span>
                    </Label>
                    <textarea
                      id="goal"
                      value={coachingGoal}
                      onChange={(e) => setCoachingGoal(e.target.value)}
                      placeholder="What brings you here? What would you like to develop?"
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none h-20"
                      maxLength={280}
                    />
                    <p className="text-xs text-shadow-pine/60 mt-1">{coachingGoal.length}/280</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-obsidian-night mb-2 block">Experience Level</Label>
                    <div className="space-y-2">
                      {EXPERIENCE_LEVELS.map(level => (
                        <button
                          key={level.value}
                          onClick={() => setExperienceLevel(level.value)}
                          className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            experienceLevel === level.value
                              ? 'border-burnt-terra bg-burnt-terra/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-left">
                            <p className="font-medium text-sm text-obsidian-night">{level.label}</p>
                            <p className="text-xs text-shadow-pine/70">{level.description}</p>
                          </div>
                          {experienceLevel === level.value && (
                            <Check className="w-4 h-4 text-burnt-terra" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Button onClick={saveProfile} disabled={loading} className="bg-burnt-terra hover:bg-burnt-terra/90">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  {saved && (
                    <span className="text-shadow-pine text-sm flex items-center gap-1">
                      <Check className="w-4 h-4" /> Saved
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <NotificationsSection 
                notifications={notifications}
                setNotifications={setNotifications}
                saveProfile={saveProfile}
                loading={loading}
              />
            )}

            {/* Accessibility Section */}
            {activeSection === 'accessibility' && (
              <div className="bg-white rounded-xl p-6 space-y-6">
                <div>
                  <h2 className="font-display text-lg font-bold text-obsidian-night mb-1">Accessibility</h2>
                  <p className="text-sm text-shadow-pine/70">Adjust the app to work better for you</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-obsidian-night mb-2 block">Font Size</Label>
                    <div className="flex gap-2">
                      {FONT_SIZES.map(size => (
                        <button
                          key={size.value}
                          onClick={() => setAccessibility({...accessibility, font_size: size.value})}
                          className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                            accessibility.font_size === size.value
                              ? 'border-burnt-terra bg-burnt-terra/5 text-burnt-terra'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {size.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="font-medium text-sm text-obsidian-night">High Contrast</p>
                      <p className="text-xs text-shadow-pine/70">Increase contrast for better visibility</p>
                    </div>
                    <Switch
                      checked={accessibility.high_contrast}
                      onCheckedChange={(checked) => setAccessibility({...accessibility, high_contrast: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="font-medium text-sm text-obsidian-night">Reduce Motion</p>
                      <p className="text-xs text-shadow-pine/70">Minimize animations and transitions</p>
                    </div>
                    <Switch
                      checked={accessibility.reduce_motion}
                      onCheckedChange={(checked) => setAccessibility({...accessibility, reduce_motion: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="font-medium text-sm text-obsidian-night">Captions</p>
                      <p className="text-xs text-shadow-pine/70">Show captions for audio/video content</p>
                    </div>
                    <Switch
                      checked={accessibility.captions_enabled}
                      onCheckedChange={(checked) => setAccessibility({...accessibility, captions_enabled: checked})}
                    />
                  </div>
                </div>

                <Button onClick={saveProfile} disabled={loading} className="bg-burnt-terra hover:bg-burnt-terra/90">
                  {loading ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            )}

            {/* Account & Security Section */}
            {activeSection === 'account' && (
              <div className="space-y-6">
                {/* Change Password */}
                <div className="bg-white rounded-xl p-6">
                  <div className="mb-4">
                    <h2 className="font-display text-lg font-bold text-obsidian-night mb-1">Change Password</h2>
                    <p className="text-sm text-shadow-pine/70">Update your password to keep your account secure</p>
                  </div>

                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <Label htmlFor="current-password" className="text-sm font-medium">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password" className="text-sm font-medium">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordForm.new}
                        onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                        className="mt-1"
                      />
                    </div>

                    {passwordError && (
                      <p className="text-red-600 text-sm">{passwordError}</p>
                    )}
                    {passwordSuccess && (
                      <p className="text-shadow-pine text-sm">{passwordSuccess}</p>
                    )}

                    <Button type="submit" variant="outline">
                      <Lock className="w-4 h-4 mr-2" />
                      Update Password
                    </Button>
                  </form>
                </div>

                {/* Delete Account */}
                <div className="bg-white rounded-xl p-6 border-2 border-red-100">
                  <div className="mb-4">
                    <h2 className="font-display text-lg font-bold text-red-700 mb-1">Delete Account</h2>
                    <p className="text-sm text-shadow-pine/70">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                  </div>

                  {!showDeleteConfirm ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete My Account
                    </Button>
                  ) : (
                    <div className="space-y-4 p-4 bg-red-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-800">Are you sure?</p>
                          <p className="text-sm text-red-700 mt-1">
                            All your data will be permanently deleted. Type <strong>DELETE</strong> to confirm.
                          </p>
                        </div>
                      </div>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="Type DELETE to confirm"
                        className="bg-white"
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleDeleteAccount}
                          disabled={deleteConfirmText !== 'DELETE'}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Permanently Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
